"use client";

import { Droplets, Hammer, Leaf, Sun, TreePine, Waves } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function LearningOutcomes() {
  const practices = [{
    icon: TreePine,
    label: "OUTCOME 01 / FOUNDATION",
    title: "Core Cognitive Development",
    body: "Our curriculum prioritizes the building blocks of critical thinking and logical reasoning. Students engage with complex problem-solving scenarios that challenge their analytical skills, ensuring they develop a robust mental framework for lifelong learning and academic excellence in any field.",
    credit: "Accredited by the Global Education Standards Board",
    colSpan: "md:col-span-5",
    offset: "md:mt-0"
  }, {
    icon: Waves,
    label: "OUTCOME 02 / EXPRESSION",
    title: "Advanced Linguistic Mastery",
    body: "We move beyond basic literacy to cultivate nuanced communication. Students explore classical literature and modern rhetoric, learning to articulate complex ideas with clarity and persuasive power. This mastery of language serves as the primary tool for leadership and social influence.",
    credit: "In collaboration with the Institute of Rhetoric",
    colSpan: "md:col-span-6",
    offset: "md:mt-20"
  }, {
    icon: Hammer,
    label: "OUTCOME 03 / CREATION",
    title: "Technical & Artistic Synthesis",
    body: "True innovation happens at the intersection of art and science. Our students participate in hands-on workshops where they apply mathematical principles to physical creation—from architectural modeling to digital design—fostering a deep appreciation for the craft of making.",
    credit: "Partnered with the Creative Arts Guild",
    colSpan: "md:col-span-4",
    offset: "md:mt-12"
  }, {
    icon: Sun,
    label: "OUTCOME 04 / AWARENESS",
    title: "Global Cultural Literacy",
    body: "In an interconnected world, understanding diverse perspectives is essential. We integrate global history and sociology into every module, encouraging students to analyze world events through multiple lenses. This builds empathy and the cultural intelligence required for global citizenship.",
    credit: "Curated by the International Studies Department",
    colSpan: "md:col-span-6",
    offset: "md:mt-0"
  }, {
    icon: Droplets,
    label: "OUTCOME 05 / WELLNESS",
    title: "Emotional Intelligence & Resilience",
    body: "Academic success is incomplete without emotional maturity. Our programs include dedicated time for mindfulness, self-reflection, and collaborative conflict resolution. Students learn to navigate stress and build healthy relationships, creating a sustainable path for personal growth.",
    credit: "Supported by the Mind-Body Wellness Initiative",
    colSpan: "md:col-span-5",
    offset: "md:mt-16"
  }, {
    icon: Leaf,
    label: "OUTCOME 06 / LEADERSHIP",
    title: "Ethical Leadership & Stewardship",
    body: "We prepare students to lead with integrity. Through community service projects and ethical debate, learners develop a strong moral compass. They graduate not just as high achievers, but as responsible stewards of their communities and the natural environment.",
    credit: "A network of mentors across diverse industries",
    colSpan: "md:col-span-6",
    offset: "md:mt-8"
  }];
  const eyebrow = useRevealOnScroll<HTMLDivElement>();
  const heading = useRevealOnScroll<HTMLHeadingElement>();
  const intro = useRevealOnScroll<HTMLParagraphElement>();
  return <section data-section-id="3208" id="learning-outcomes" className="relative py-40 px-6 md:px-12 lg:px-20 overflow-hidden bg-background text-foreground">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-10 mb-24">
          <div ref={eyebrow.ref} className={`md:col-span-12 transition-all duration-1000 ease-out ${eyebrow.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <span className="text-xs font-mono uppercase text-primary" style={{
            letterSpacing: "0.25em"
          }}>
              — Educational Excellence
            </span>
          </div>

          <h2 ref={heading.ref} className={`md:col-span-9 font-default font-light leading-[1.05] transition-all duration-1000 delay-150 ease-out text-foreground ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          fontSize: "clamp(3rem, 5vw, 5rem)"
        }}>
            Skills that{" "}
            <em className="italic font-light text-secondary">
              endure
            </em>{" "}
            forever.
          </h2>

          <div className="md:col-span-12 md:grid md:grid-cols-12 md:gap-8 mt-6">
            <p ref={intro.ref} className={`md:col-start-1 md:col-span-7 lg:col-span-6 text-lg leading-relaxed font-default transition-all duration-1000 delay-300 ease-out text-muted-foreground ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              The foundation of our pedagogy. These are the core competencies we instill in every student — 
              deliberate outcomes that define how they think, communicate, and lead. None of them are 
              accidental. All of them are essential.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {practices.map((practice, i) => {
          const card = useRevealOnScroll<HTMLDivElement>();
          const Icon = practice.icon;
          return <div key={i} data-index={i} ref={card.ref} className={`${practice.colSpan} ${practice.offset} group rounded-md p-10 transition-all duration-1000 ease-out shadow-md bg-card border border-border ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 80}ms`
          }}>
                <div className="mb-8">
                  <Icon className="w-8 h-8 transition-all duration-500 ease-out group-hover:-translate-y-1 text-primary" strokeWidth={1} />
                  <span className="block w-8 h-px mt-2 transition-all duration-500 group-hover:opacity-100 opacity-0" style={{
                background: "radial-gradient(ellipse at center, var(--secondary) 0%, transparent 70%)",
                filter: "blur(4px)"
              }} aria-hidden="true" />
                </div>

                <div className="text-xs font-mono uppercase mb-5 text-primary" style={{
              letterSpacing: "0.25em"
            }}>
                  {practice.label}
                </div>

                <h3 className="font-default font-semibold mb-5 leading-tight text-foreground" style={{
              fontSize: "1.75rem"
            }}>
                  {practice.title}
                </h3>

                <p className="font-default text-base leading-relaxed mb-8 text-muted-foreground">
                  {practice.body}
                </p>

                
              </div>;
        })}
        </div>
      </div>
    </section>;
}